import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rolesQueries } from '@/_api/roles/query';

interface UsePermissionsReturn {
  permissions: string[];
  permissionsLoading: boolean;
  hasPermission: (requiredPermissions: string | string[]) => boolean;
  hasOrganizationId: boolean;
  isAdmin: boolean;
}

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const usePermissions = (): UsePermissionsReturn => {
  const authState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('authState') || '{}');
    } catch {
      return {};
    }
  }, []);

  const token = authState?.accessToken;

  const { isAdmin, hasOrganizationId } = useMemo(() => {
    let _isAdmin = false;
    let _hasOrg = false;

    if (token) {
      const decoded: any = decodeJWT(token);

      _isAdmin = Array.isArray(decoded?.role)
        ? decoded.role.includes('admin')
        : decoded?.role === 'admin';

      const orgId = decoded?.organizationId;
      _hasOrg = Array.isArray(orgId) ? orgId.length > 0 : orgId != null;
    }

    return { isAdmin: _isAdmin, hasOrganizationId: _hasOrg };
  }, [token]);

  const { data: apiPermissions, isLoading } = useQuery<string[]>({
    queryKey: rolesQueries.keys.userPermissions(),
    queryFn: rolesQueries.fn.fetchUserPermissions,
    enabled: Boolean(token) && !isAdmin,
  });

  const permissions = useMemo<string[]>(() => {
    if (isAdmin) {
      return ['*'];
    }
    return apiPermissions ?? [];
  }, [isAdmin, apiPermissions]);

  const hasPermission = (requiredPermissions: string | string[]): boolean => {
    if (isAdmin || permissions.includes('*')) {
      return true;
    }

    const toCheck = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];

    return toCheck.some((p) => permissions.includes(p));
  };

  // ✅ loading: sadece ilk yüklemede true
  const permissionsLoading = !isAdmin && isLoading;

  return { permissions, permissionsLoading, hasPermission, hasOrganizationId, isAdmin };
};
